import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import ImageUpload from './ImageUpload';
import { 
  Bold, Italic, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Image as ImageIcon, Heading1, Heading2,
  Undo, Redo, Eye, Edit, Send, Save, X, Users, Mail,
  Layout, FileText, Megaphone, Heart, CheckCircle
} from 'lucide-react';

const NEWSLETTER_TEMPLATES = {
  general: {
    name: 'General Update',
    icon: FileText,
    color: 'bg-blue-100 text-blue-700',
    content: `
      <h1 style="text-align: center;">Lions Unleashed Newsletter</h1>
      <p style="text-align: center; color: #6b7280;">Monthly Update</p>
      <hr/>
      <h2>Hello from Lions Unleashed!</h2>
      <p>We're excited to share the latest updates with you.</p>
      <h3>What's New</h3>
      <ul>
        <li>Update item 1</li>
        <li>Update item 2</li>
        <li>Update item 3</li>
      </ul>
      <h3>Upcoming Events</h3>
      <p>Stay tuned for our upcoming events...</p>
      <hr/>
      <p style="text-align: center; color: #6b7280; font-size: 0.875rem;">
        Lions Unleashed | <a href="#">Unsubscribe</a>
      </p>
    `
  },
  announcement: {
    name: 'Announcement',
    icon: Megaphone,
    color: 'bg-amber-100 text-amber-700',
    content: `
      <div style="text-align: center; padding: 2rem 0;">
        <h1 style="color: #0891b2;">📢 Important Announcement</h1>
      </div>
      <hr/>
      <h2>Big News!</h2>
      <p>We have an exciting announcement to share with you...</p>
      <div style="background: #f0f9ff; padding: 1.5rem; border-radius: 0.5rem; margin: 1rem 0;">
        <h3 style="margin-top: 0;">Key Highlights</h3>
        <ul>
          <li>Highlight 1</li>
          <li>Highlight 2</li>
          <li>Highlight 3</li>
        </ul>
      </div>
      <p style="text-align: center;">
        <a href="#" style="background: #0891b2; color: white; padding: 0.75rem 2rem; border-radius: 0.5rem; text-decoration: none; display: inline-block;">Learn More</a>
      </p>
      <hr/>
      <p style="text-align: center; color: #6b7280; font-size: 0.875rem;">
        Lions Unleashed | <a href="#">Unsubscribe</a>
      </p>
    `
  },
  donation_appeal: {
    name: 'Donation Appeal',
    icon: Heart,
    color: 'bg-red-100 text-red-700',
    content: `
      <div style="text-align: center; padding: 2rem 0;">
        <h1 style="color: #dc2626;">❤️ Your Support Matters</h1>
        <p style="font-size: 1.25rem; color: #6b7280;">Help us make a difference</p>
      </div>
      <hr/>
      <h2>Dear Supporter,</h2>
      <p>Your generous contributions have helped us achieve so much. Today, we're reaching out to ask for your continued support.</p>
      <div style="background: #fef2f2; padding: 1.5rem; border-radius: 0.5rem; margin: 1rem 0; text-align: center;">
        <h3 style="margin-top: 0; color: #dc2626;">Our Impact So Far</h3>
        <p style="font-size: 2rem; font-weight: bold; color: #dc2626;">500+</p>
        <p>Lives Changed</p>
      </div>
      <p>Every dollar makes a difference. Your donation helps us:</p>
      <ul>
        <li>Support underprivileged youth</li>
        <li>Provide mentorship programs</li>
        <li>Create opportunities for growth</li>
      </ul>
      <p style="text-align: center; margin: 2rem 0;">
        <a href="#" style="background: #dc2626; color: white; padding: 1rem 2.5rem; border-radius: 0.5rem; text-decoration: none; display: inline-block; font-weight: bold;">Donate Now</a>
      </p>
      <p style="text-align: center;">Thank you for being part of our pride! 🦁</p>
      <hr/>
      <p style="text-align: center; color: #6b7280; font-size: 0.875rem;">
        Lions Unleashed | <a href="#">Unsubscribe</a>
      </p>
    `
  },
  success_story: {
    name: 'Success Story',
    icon: CheckCircle,
    color: 'bg-green-100 text-green-700',
    content: `
      <div style="text-align: center; padding: 2rem 0;">
        <h1 style="color: #059669;">🌟 Success Story</h1>
        <p style="color: #6b7280;">Celebrating Our Community</p>
      </div>
      <hr/>
      <div style="text-align: center; margin: 2rem 0;">
        <img src="https://via.placeholder.com/400x250" alt="Success Story" style="border-radius: 0.5rem; max-width: 100%;"/>
      </div>
      <h2>Meet [Name]</h2>
      <p><em>"Quote from the person..."</em></p>
      <p>Share the inspiring story here...</p>
      <div style="background: #ecfdf5; padding: 1.5rem; border-radius: 0.5rem; margin: 1rem 0;">
        <h3 style="margin-top: 0; color: #059669;">Journey Highlights</h3>
        <ul>
          <li>Started as a Cub in 2023</li>
          <li>Received mentorship from experienced Lions</li>
          <li>Now thriving in their career</li>
        </ul>
      </div>
      <p style="text-align: center;">
        <a href="#" style="background: #059669; color: white; padding: 0.75rem 2rem; border-radius: 0.5rem; text-decoration: none; display: inline-block;">Read More Stories</a>
      </p>
      <hr/>
      <p style="text-align: center; color: #6b7280; font-size: 0.875rem;">
        Lions Unleashed | <a href="#">Unsubscribe</a>
      </p>
    `
  }
};

const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-40 ${
      isActive ? 'bg-cyan-100 text-cyan-700' : 'text-gray-600'
    }`}
  >
    {children}
  </button>
);

const MenuBar = ({ editor, onImageUploadClick }) => {
  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('Enter URL:', 'https://');
    if (url) editor.chain().focus().setLink({ href: url }).run();
  };

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b bg-gray-50">
      <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} isActive={editor.isActive('heading', { level: 1 })} title="Heading 1">
        <Heading1 size={16} />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Heading 2">
        <Heading2 size={16} />
      </MenuButton>
      <div className="w-px h-6 bg-gray-300 mx-1" />
      <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Bold">
        <Bold size={16} />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Italic">
        <Italic size={16} />
      </MenuButton>
      <div className="w-px h-6 bg-gray-300 mx-1" />
      <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Bullet List">
        <List size={16} />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Numbered List">
        <ListOrdered size={16} />
      </MenuButton>
      <div className="w-px h-6 bg-gray-300 mx-1" />
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Align Left">
        <AlignLeft size={16} />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Align Center">
        <AlignCenter size={16} />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Align Right">
        <AlignRight size={16} />
      </MenuButton>
      <div className="w-px h-6 bg-gray-300 mx-1" />
      <MenuButton onClick={addLink} isActive={editor.isActive('link')} title="Add Link">
        <LinkIcon size={16} />
      </MenuButton>
      <MenuButton onClick={onImageUploadClick} title="Insert Image">
        <ImageIcon size={16} />
      </MenuButton>
      <div className="w-px h-6 bg-gray-300 mx-1" />
      <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo size={16} />
      </MenuButton>
      <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo size={16} />
      </MenuButton>
    </div>
  );
};

const NewsletterBuilder = ({ newsletter, onSave, onCancel, subscriberStats }) => {
  const [activeTab, setActiveTab] = useState('edit');
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [formData, setFormData] = useState({
    subject: newsletter?.subject || '',
    content: newsletter?.content || '',
    template: newsletter?.template || 'general',
    recipientType: newsletter?.recipientType || 'all'
  });
  const [showTemplates, setShowTemplates] = useState(!newsletter);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg max-w-full' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  const applyTemplate = (templateKey) => {
    const template = NEWSLETTER_TEMPLATES[templateKey];
    if (editor) {
      editor.commands.setContent(template.content);
    }
    setFormData(prev => ({
      ...prev,
      template: templateKey,
      content: template.content
    }));
    setShowTemplates(false);
  };

  const getRecipientCount = () => {
    if (!subscriberStats) return 0;
    switch (formData.recipientType) {
      case 'subscribers':
        return subscriberStats.active || 0;
      case 'donors':
        return subscriberStats.totalReach?.donors || 0;
      case 'applicants':
        return subscriberStats.totalReach?.applicants || 0;
      case 'all':
      default:
        return (subscriberStats.active || 0) + 
               (subscriberStats.totalReach?.donors || 0) + 
               (subscriberStats.totalReach?.applicants || 0);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Mail className="w-5 h-5" />
          {newsletter ? 'Edit Newsletter' : 'Create Newsletter'}
        </h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-2" /> Cancel
          </Button>
          <Button variant="outline" onClick={() => onSave({ ...formData, status: 'draft' })}>
            <Save className="w-4 h-4 mr-2" /> Save Draft
          </Button>
        </div>
      </div>

      {/* Template Selection */}
      {showTemplates && (
        <div className="p-6 border-b bg-gray-50">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Layout className="w-4 h-4" /> Choose a Template
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {Object.entries(NEWSLETTER_TEMPLATES).map(([key, template]) => {
              const Icon = template.icon;
              return (
                <button
                  key={key}
                  onClick={() => applyTemplate(key)}
                  className={`p-4 border-2 rounded-lg hover:border-cyan-500 transition-all text-left ${
                    formData.template === key ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg ${template.color} flex items-center justify-center mb-2`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h4 className="font-medium">{template.name}</h4>
                </button>
              );
            })}
          </div>
          <div className="mt-4 text-center">
            <Button variant="ghost" onClick={() => setShowTemplates(false)}>
              Start from scratch
            </Button>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Main Editor Area */}
        <div className="flex-1">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="border-b px-4 flex items-center justify-between">
              <TabsList className="h-12">
                <TabsTrigger value="edit" className="flex items-center gap-2">
                  <Edit className="w-4 h-4" /> Edit
                </TabsTrigger>
                <TabsTrigger value="preview" className="flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Preview
                </TabsTrigger>
              </TabsList>
              {!showTemplates && (
                <Button variant="ghost" size="sm" onClick={() => setShowTemplates(true)}>
                  <Layout className="w-4 h-4 mr-2" /> Templates
                </Button>
              )}
            </div>

            <TabsContent value="edit" className="m-0">
              {/* Subject Line */}
              <div className="p-4 border-b">
                <Label className="text-sm font-semibold">Subject Line</Label>
                <Input
                  value={formData.subject}
                  onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                  placeholder="Enter email subject line..."
                  className="mt-1 text-lg"
                />
              </div>

              {/* Editor */}
              <div>
                <MenuBar 
                  editor={editor} 
                  onImageUploadClick={() => setShowImageUpload(true)}
                />
                <EditorContent 
                  editor={editor} 
                  className="prose max-w-none p-6 min-h-[400px] focus:outline-none [&_.ProseMirror]:outline-none"
                />
              </div>
            </TabsContent>

            <TabsContent value="preview" className="m-0">
              {/* Email Preview */}
              <div className="p-6 bg-gray-100 min-h-[500px]">
                <div className="max-w-xl mx-auto">
                  {/* Email Client Mock */}
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-gray-800 text-white p-3 flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                      </div>
                      <span className="text-sm ml-2">Email Preview</span>
                    </div>
                    <div className="p-4 border-b bg-gray-50">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-full bg-cyan-500 flex items-center justify-center text-white font-bold">LU</div>
                        <div>
                          <p className="font-semibold">Lions Unleashed</p>
                          <p className="text-sm text-gray-500">info@lionsunleashed.org</p>
                        </div>
                      </div>
                      <p className="font-semibold text-lg">{formData.subject || '(No subject)'}</p>
                    </div>
                    <div 
                      className="p-6 prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: formData.content || '<p class="text-gray-400">Start editing to see preview...</p>' }}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Sidebar */}
        <div className="w-72 border-l p-4 space-y-6 bg-gray-50">
          {/* Recipients */}
          <div>
            <Label className="text-sm font-semibold flex items-center gap-2">
              <Users className="w-4 h-4" /> Recipients
            </Label>
            <Select value={formData.recipientType} onValueChange={(value) => setFormData(prev => ({ ...prev, recipientType: value }))}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Contacts</SelectItem>
                <SelectItem value="subscribers">Subscribers Only</SelectItem>
                <SelectItem value="donors">Donors Only</SelectItem>
                <SelectItem value="applicants">Applicants Only</SelectItem>
              </SelectContent>
            </Select>
            <div className="mt-3 p-3 bg-white rounded-lg border">
              <p className="text-2xl font-bold text-cyan-600">{getRecipientCount()}</p>
              <p className="text-sm text-gray-500">recipients will receive this email</p>
            </div>
          </div>

          {/* Template Info */}
          <div>
            <Label className="text-sm font-semibold">Template</Label>
            <div className="mt-2 p-3 bg-white rounded-lg border flex items-center gap-3">
              {(() => {
                const template = NEWSLETTER_TEMPLATES[formData.template];
                const Icon = template?.icon || FileText;
                return (
                  <>
                    <div className={`w-8 h-8 rounded ${template?.color || 'bg-gray-100'} flex items-center justify-center`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="font-medium">{template?.name || 'Custom'}</span>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="pt-4 border-t">
            <Label className="text-sm font-semibold">Audience Breakdown</Label>
            <div className="mt-2 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subscribers</span>
                <span className="font-medium">{subscriberStats?.active || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Donors</span>
                <span className="font-medium">{subscriberStats?.totalReach?.donors || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Applicants</span>
                <span className="font-medium">{subscriberStats?.totalReach?.applicants || 0}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="pt-4 border-t space-y-2">
            <Button 
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-600"
              onClick={() => onSave({ ...formData, status: 'draft', sendNow: true })}
            >
              <Send className="w-4 h-4 mr-2" /> Save & Send Now
            </Button>
          </div>
        </div>
      </div>

      <style>{`
        .ProseMirror { outline: none; min-height: 350px; }
        .ProseMirror img { max-width: 100%; height: auto; border-radius: 0.5rem; }
        .ProseMirror h1 { font-size: 1.875rem; font-weight: 700; margin: 1rem 0; }
        .ProseMirror h2 { font-size: 1.5rem; font-weight: 600; margin: 0.75rem 0; }
        .ProseMirror h3 { font-size: 1.25rem; font-weight: 600; margin: 0.5rem 0; }
        .ProseMirror p { margin: 0.5rem 0; }
        .ProseMirror ul, .ProseMirror ol { padding-left: 1.5rem; margin: 0.5rem 0; }
        .ProseMirror hr { border: none; border-top: 1px solid #e5e7eb; margin: 1rem 0; }
        .ProseMirror a { color: #0891b2; text-decoration: underline; }
      `}</style>

      {/* Image Upload Dialog */}
      <ImageUpload
        open={showImageUpload}
        onClose={() => setShowImageUpload(false)}
        onImageSelect={(url) => {
          if (editor) {
            editor.chain().focus().setImage({ src: url }).run();
          }
        }}
      />
    </div>
  );
};

export default NewsletterBuilder;
